// Google Identity Services' web script (window.google.accounts.id) relies on a
// real top-level browser context and is blocked/unreliable inside a Capacitor
// WebView. Inside the native shell we use Capacitor Social Login instead, which
// returns the same kind of Google ID token, so callers can feed it into the
// existing credential-response handlers unchanged.
export const isNativeMobileApp = () => Boolean(window?.Capacitor?.isNativePlatform?.());

// Thrown for a user-initiated cancel (closed the native account chooser) so
// callers can show "sign-in cancelled" instead of an error banner.
export const GOOGLE_SIGNIN_CANCELLED = 'GOOGLE_SIGNIN_CANCELLED';

// Shown instead of any raw plugin/framework error (unregistered native
// plugin, missing OAuth client config, network failure, etc.) — a customer
// should never see a raw native SDK error string.
export const GOOGLE_SIGNIN_FRIENDLY_ERROR = 'Google sign-in isn’t available right now. Please try again or sign in with email.';
export const GOOGLE_SIGNIN_NETWORK_ERROR = 'No connection. Check your network and try again.';

// Dev-only diagnostics — booleans/status codes only, never the token itself,
// never account details — so whoever is running a demo build can tell from
// the device log console exactly which stage failed (plugin missing vs.
// init failed vs. no token returned) instead of guessing from one generic banner.
const logStage = (stage, detail) => {
    // eslint-disable-next-line no-console
    console.log(`[auth][google] ${stage}`, detail ?? '');
};

export const signInWithGoogleNative = async () => {
    let SocialLogin;
    try {
        ({ SocialLogin } = await import('@capgo/capacitor-social-login'));
        logStage('plugin:loaded');
    } catch (err) {
        logStage('plugin:unavailable', err?.message);
        throw new Error(GOOGLE_SIGNIN_FRIENDLY_ERROR);
    }

    const webClientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || '';
    const iOSClientId = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID || '';

    try {
        await SocialLogin.initialize({
            google: {
                webClientId,
                ...(iOSClientId ? { iOSClientId } : {}),
                mode: 'online',
            },
        });
        logStage('init:success');
    } catch (err) {
        logStage('init:failed', err?.message);
        throw new Error(GOOGLE_SIGNIN_FRIENDLY_ERROR);
    }

    let response;
    try {
        response = await SocialLogin.login({
            provider: 'google',
            options: {
                scopes: ['email', 'profile'],
            },
        });
        logStage('signIn:success', { hasIdToken: Boolean(response?.result?.idToken) });
    } catch (err) {
        const message = String(err?.message || '');
        const isCancelled = /cancel/i.test(message);
        logStage('signIn:failed', { message });

        if (isCancelled) {
            throw new Error(GOOGLE_SIGNIN_CANCELLED);
        }
        if (/network|offline/i.test(message)) {
            throw new Error(GOOGLE_SIGNIN_NETWORK_ERROR);
        }
        throw new Error(GOOGLE_SIGNIN_FRIENDLY_ERROR);
    }

    const idToken = response?.result?.idToken;
    if (!idToken) {
        logStage('signIn:no-id-token');
        throw new Error(GOOGLE_SIGNIN_FRIENDLY_ERROR);
    }

    return idToken;
};
